/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export type ProductFeatures = {
  /**
   * Whether the organization can provision push integrations for AI platforms
   */
  aiPlatformPushIntegrationsEnabled: boolean;
  /**
   * Whether authz challenge logging to ClickHouse is enabled
   */
  authzChallengeLoggingEnabled: boolean;
  /**
   * Whether the organization has the staff-managed gateway Code Mode entitlement
   */
  codeModeEnabled: boolean;
  /**
   * Whether the organization can supply its own model provider API keys (BYOK)
   */
  customModelKeysEnabled: boolean;
  /**
   * Whether the organization can manage the external credentials and cloud KMS keys backing customer-managed encryption
   */
  customerManagedEncryptionKeysEnabled: boolean;
  /**
   * Whether the organization uses the device agent (any device has polled agent.getPlugins). Derived from device-agent syncs, not an admin-settable feature.
   */
  deviceAgent: boolean;
  /**
   * Whether generated hook plugins may mint per-user keys via the interactive browser login
   */
  hooksBrowserLoginEnabled: boolean;
  /**
   * Whether hooks fail open when the Speakeasy control plane is unreachable or erroring — blocking policies are not enforced for the duration of the outage
   */
  hooksFailOpenEnabled: boolean;
  /**
   * Whether logging is enabled
   */
  logsEnabled: boolean;
  /**
   * Whether the organization has the staff-managed private network ingress entitlement
   */
  networkIngressEnabled: boolean;
  /**
   * Whether an organization admin turned the organization gateway off, so its routes answer 404
   */
  organizationGatewayDisabled: boolean;
  /**
   * Whether the organization can use the Speakeasy Platform MCP capability
   */
  platformMcpEnabled: boolean;
  /**
   * Whether the staff-managed provider setup rollout is enabled. Defaults to false when absent.
   */
  providerSetupEnabled?: boolean | undefined;
  /**
   * Whether consent screens expose automatic remote-session refresh for the organization
   */
  remoteSessionAutoRefreshEnabled: boolean;
  /**
   * Whether automatic remote-session refresh is enforced as the organization default: forced on for every user, shown locked on consent screens, and applied by the keepalive regardless of per-session preference
   */
  remoteSessionAutoRefreshEnforcedEnabled: boolean;
  /**
   * Whether SCIM/directory sync setup is enabled for the organization
   */
  scimEnabled: boolean;
  /**
   * Whether Claude Code session capture is enabled
   */
  sessionCaptureEnabled: boolean;
  /**
   * Whether agent session portability is enabled for the organization: session sharing links, move reporting with lineage, and picker title enrichment via the device agent
   */
  sessionPortabilityEnabled: boolean;
  /**
   * Whether the organization can configure signals intelligence
   */
  signalsIntelligenceEnabled: boolean;
  /**
   * Whether skill capture stores activation metadata without requesting manifest content
   */
  skillCaptureMetadataOnly: boolean;
  /**
   * Whether the Skills page is enabled for the organization
   */
  skillsEnabled: boolean;
  /**
   * Whether SSO setup is enabled for the organization
   */
  ssoEnabled: boolean;
  /**
   * Whether tool I/O logging is enabled
   */
  toolIoLogsEnabled: boolean;
  /**
   * Whether upstream auth enforcement is on for the organization. Absent when the state could not be read. Staff change it through the admin enforcement endpoint.
   */
  upstreamAuthEnforced?: boolean | undefined;
};

/** @internal */
export const ProductFeatures$inboundSchema: z.ZodMiniType<
  ProductFeatures,
  unknown
> = z.pipe(
  z.object({
    ai_platform_push_integrations_enabled: z.boolean(),
    authz_challenge_logging_enabled: z.boolean(),
    code_mode_enabled: z.boolean(),
    custom_model_keys_enabled: z.boolean(),
    customer_managed_encryption_keys_enabled: z.boolean(),
    device_agent: z.boolean(),
    hooks_browser_login_enabled: z.boolean(),
    hooks_fail_open_enabled: z.boolean(),
    logs_enabled: z.boolean(),
    network_ingress_enabled: z.boolean(),
    organization_gateway_disabled: z.boolean(),
    platform_mcp_enabled: z.boolean(),
    provider_setup_enabled: z.optional(z.boolean()),
    remote_session_auto_refresh_enabled: z.boolean(),
    remote_session_auto_refresh_enforced_enabled: z.boolean(),
    scim_enabled: z.boolean(),
    session_capture_enabled: z.boolean(),
    session_portability_enabled: z.boolean(),
    signals_intelligence_enabled: z.boolean(),
    skill_capture_metadata_only: z.boolean(),
    skills_enabled: z.boolean(),
    sso_enabled: z.boolean(),
    tool_io_logs_enabled: z.boolean(),
    upstream_auth_enforced: z.optional(z.boolean()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "ai_platform_push_integrations_enabled":
        "aiPlatformPushIntegrationsEnabled",
      "authz_challenge_logging_enabled": "authzChallengeLoggingEnabled",
      "code_mode_enabled": "codeModeEnabled",
      "custom_model_keys_enabled": "customModelKeysEnabled",
      "customer_managed_encryption_keys_enabled":
        "customerManagedEncryptionKeysEnabled",
      "device_agent": "deviceAgent",
      "hooks_browser_login_enabled": "hooksBrowserLoginEnabled",
      "hooks_fail_open_enabled": "hooksFailOpenEnabled",
      "logs_enabled": "logsEnabled",
      "network_ingress_enabled": "networkIngressEnabled",
      "organization_gateway_disabled": "organizationGatewayDisabled",
      "platform_mcp_enabled": "platformMcpEnabled",
      "provider_setup_enabled": "providerSetupEnabled",
      "remote_session_auto_refresh_enabled": "remoteSessionAutoRefreshEnabled",
      "remote_session_auto_refresh_enforced_enabled":
        "remoteSessionAutoRefreshEnforcedEnabled",
      "scim_enabled": "scimEnabled",
      "session_capture_enabled": "sessionCaptureEnabled",
      "session_portability_enabled": "sessionPortabilityEnabled",
      "signals_intelligence_enabled": "signalsIntelligenceEnabled",
      "skill_capture_metadata_only": "skillCaptureMetadataOnly",
      "skills_enabled": "skillsEnabled",
      "sso_enabled": "ssoEnabled",
      "tool_io_logs_enabled": "toolIoLogsEnabled",
      "upstream_auth_enforced": "upstreamAuthEnforced",
    });
  }),
);

export function productFeaturesFromJSON(
  jsonString: string,
): SafeParseResult<ProductFeatures, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ProductFeatures$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ProductFeatures' from JSON`,
  );
}
